import { memo, useMemo } from 'react';
import { ClanTag, Highlighter, Realm } from '@wg/wows-react-uikit';

import settings, { hasCrossRealm } from '~/settings';
import { thousands } from '~/helpers/formatting';
import { t } from '~/helpers/localization';

import styles from './VersusHeader.scss';
import Medal from '../Medal/Medal';

import type { WarSettingsLeague } from '~/types/declaration';

export type ISideData = {
  id?: number | string;
  tag: string;
  color: string;
  realm: string;
  score: number;
  leagueData: WarSettingsLeague;
};

interface VersusHeaderProps {
  left: ISideData;
  right?: ISideData;
  withMedal?: boolean;
  group?: ISideData[];
}

const VersusHeader = ({ left, right, withMedal = false, group }: VersusHeaderProps) => {
  const placeInGroup = useMemo(() => {
    if (!group) {
      return null;
    }

    const allClans = [...group, left].sort((a, b) => b.score - a.score);

    let place = 1;

    for (const [index, clan] of allClans.entries()) {
      if (index > 0 && clan.score !== allClans[index - 1].score) {
        place += 1;
      }

      if (clan.id === left.id) {
        break;
      }
    }

    return place;
  }, [group, left]);

  const renderSide = (data: ISideData | undefined, isReverse: boolean) => {
    if (!data) {
      return null;
    }

    return (
      <div className={isReverse ? styles.sideWrapperReverse : styles.sideWrapper}>
        {withMedal ? (
          <div className={styles.medal}>
            <Medal size="small" league={data.leagueData.number} />
          </div>
        ) : null}

        <div className={isReverse ? styles.tagRealmWrapperReverse : styles.tagRealmWrapper}>
          <div className={styles.tag}>
            <ClanTag clanTag={data.tag} clanColor={data.color || '#fff'} />
          </div>
          {hasCrossRealm ? <Realm realm={data.realm} realmMap={settings.realmsMap} /> : null}
        </div>

        <div className={styles.starScoreWrapper}>
          <div className={styles.scoreWrapper}>
            <Highlighter className={styles.score} value={thousands(data.score)} />
          </div>
          <div className={styles.star} />
        </div>
      </div>
    );
  };

  const renderGroup = (isReverse: boolean) => {
    if (placeInGroup === null) {
      return null;
    }

    return (
      <div className={isReverse ? styles.sideWrapperReverse : styles.sideWrapper}>
        <div className={styles.group}>
          <div className={styles.starScoreWrapper}>
            <div className={styles.scoreWrapper}>
              <Highlighter className={styles.score} value={placeInGroup} />
            </div>
          </div>
          <div className={styles.description}>{t('Место \nв группе')}</div>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.container}>
      {renderSide(left, false)}
      <div className={styles.middleSeparator} />
      {group ? renderGroup(false) : renderSide(right, true)}
    </div>
  );
};

export default memo(VersusHeader);
